/**
 * Retest Button (F1)
 *
 * Per-finding, budget-gated re-validation control for a single Open finding.
 * Customer clicks it to re-run the cato agent against the finding by enqueuing a
 * normal cato triage job (`POST /job/` with `config.source = "customer-retest"`,
 * see {@link useRetestRisk}). Cato actions the finding directly like any triage —
 * resolving it if remediated, leaving it open if still present.
 *
 * Visibility: rendered only when exactly one finding is in context AND it is
 * DEMONSTRATED (`isDemonstrated(status)` — Open AND not an Exposure). The vuln
 * drawer hosts it where a single `risk` is in scope; the bulk
 * `VulnerabilityActions` bar deliberately does NOT own it (it is selection /
 * multi-row oriented).
 *
 * Gating: when the Bifrost budget is not provisioned (`!provisioned` from
 * {@link useBifrostBudget}) the button is disabled and wrapped in a Tooltip
 * (via a focusable `<span>` so the hint shows on a disabled control — same
 * pattern as SettingEditableCard's lockedTooltip). While pending it shows a
 * spinner + "Retesting…" and is disabled.
 *
 * @module sections/vulnerabilities/components/actions
 */

import { Loader2, RotateCw } from 'lucide-react';
import { useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBifrostBudget } from '@/hooks/useAIBudget';
import { useFlag } from '@/hooks/useFlags';
import { useRetestRisk } from '@/hooks/useRisks';
import { Risk } from '@/types';
import { formatAttackSurfaceLabel } from '@/utils/mappings.util';
import { isDemonstrated } from '@/utils/riskStatus.util';

const BUDGET_TOOLTIP =
  'Provision a Bifrost AI spend budget to retest findings. Contact your administrator to set one up.';

export interface RetestButtonProps {
  /** Findings currently in context. The button only renders for a single DEMONSTRATED finding. */
  vulnerabilities: Risk[];
}

/**
 * Renders the Retest button for a single DEMONSTRATED finding, or nothing otherwise.
 */
export function RetestButton({ vulnerabilities }: RetestButtonProps) {
  const { provisioned } = useBifrostBudget();
  const { mutate: retest, isPending } = useRetestRisk();
  const { enableAIAgents } = useFlag();
  const [aiAgentsDialogOpen, setAiAgentsDialogOpen] = useState(false);

  // Only a single DEMONSTRATED finding may be retested.
  if (vulnerabilities.length !== 1) {
    return null;
  }
  const risk = vulnerabilities[0];
  if (!isDemonstrated(risk.status)) {
    return null;
  }
  // cato-agent cannot run Aegis capabilities and has no access to internal
  // environments, so retesting an internal finding would just echo the
  // original scanner evidence back as "still open" — misleading to
  // customers. Hide (not disable) the button until a proper internal
  // retest path ships (Aegis 2.0). See ENG-4312.
  // Attack-surface values may be capitalized and integration-suffixed
  // (e.g. "Internal#wiz" inherited from an asset), so normalize each value
  // through formatAttackSurfaceLabel — the same abstraction the drawer uses
  // to display them — before comparing.
  const isInternalFinding = risk.attackSurface?.some(
    surface => formatAttackSurfaceLabel(surface) === 'Internal'
  );
  if (isInternalFinding) {
    return null;
  }

  const disabled = isPending || !provisioned;

  // Soft-gate on the AI-agents flag: the button stays visible/enabled when the
  // finding is DEMONSTRATED and the budget is provisioned even with the flag
  // OFF, but a click opens an informational dialog instead of firing the
  // retest. The flag does NOT affect `disabled`.
  const handleClick = () => {
    if (enableAIAgents) {
      retest({ key: risk.key });
    } else {
      setAiAgentsDialogOpen(true);
    }
  };

  const button = (
    <Button
      className="rounded-full"
      size="sm"
      disabled={disabled}
      onClick={handleClick}
      data-testid="vulnerability-retest-button"
    >
      {isPending ? (
        <>
          <Loader2 className="size-4 motion-safe:animate-spin" />
          Retesting…
        </>
      ) : (
        <>
          <RotateCw className="size-4" />
          Retest
        </>
      )}
    </Button>
  );

  // Budget not provisioned: keep the affordance visible but disabled, with an
  // explanatory tooltip. The focusable span lets the tooltip fire on a disabled
  // button (mirrors SettingEditableCard.tsx:227-234).
  const content = !provisioned ? (
    <Tooltip title={BUDGET_TOOLTIP}>
      <span tabIndex={0} data-testid="vulnerability-retest-locked">
        {button}
      </span>
    </Tooltip>
  ) : (
    button
  );

  return (
    <>
      {content}
      <Dialog open={aiAgentsDialogOpen} onOpenChange={setAiAgentsDialogOpen}>
        <DialogContent data-testid="retest-ai-agents-required-dialog">
          <DialogHeader>
            <DialogTitle>AI Agents required</DialogTitle>
            <DialogDescription>
              Retesting is powered by automated agents, which aren&apos;t
              enabled for this account. Enable them in settings or contact your
              administrator to unlock retesting.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
